import { Box, Typography, InputBase } from "@mui/material";
import { styles } from "@/pages/Settings/stylesSettings";
import MentorResultItem from "./MentorResultItem";
import { useCallback, useEffect, useRef, useState } from "react";
import ArrowSvgRight from "@/assets/svg/ArrowIcon";
import { useLazyGetUniversalSearchQuery, UserResponse } from "@/store/api/userApi";
import theme from "@/theme";
type Props = {
  onSelect: (usernames: string) => void;
  defaultValue?: string;
  onInputChange?: (value: string) => void;
  inputValue?: string;
};

const TradingMentors: React.FC<Props> = ({ onSelect, defaultValue, onInputChange, inputValue }) => {
  const [query, setQuery] = useState("");
  type SelectedMentor = {
    label: string;
    isTag: boolean;
  };
  const [selected, setSelected] = useState<SelectedMentor[]>([]);
  const trimmedQuery = query.trim();
  const isTagSearch = trimmedQuery.startsWith("@");
  const actualQuery = isTagSearch ? trimmedQuery.slice(1) : trimmedQuery;
  const [triggerSearch] = useLazyGetUniversalSearchQuery();
  const [foundUsers, setFoundUsers] = useState<UserResponse[]>([]);
  const [notFound, setNotFound] = useState(false);
  const lastDefaultRef = useRef<string | undefined>(undefined);

  const commitSegment = (segment: string) => {
    const trimmed = segment.trim();
    if (!trimmed) return;
    const isTag = trimmed.startsWith("@");
    const plain = trimmed.replace(/^@/, "").replace(/;/g, "");
    if (selected.some((s) => s.label.replace(/^@/, "") === plain)) return;
    const valueToAdd: SelectedMentor = {
      label: isTag ? `@${plain}` : plain,
      isTag,
    };
    const updated = [...selected, valueToAdd];
    setSelected(updated);
    onSelect(format(updated));

    setQuery("");
    setFoundUsers([]);
    setNotFound(false);
  };

  const handleInputChange = useCallback(
    async (raw: string) => {
      const prevSegment = query.trim();
      const next = toCurrentSegment(raw);
      setQuery(next);
      if (onInputChange) onInputChange(next);
      if (raw.endsWith(";") || raw.endsWith(" ")) {
        if (prevSegment.length > 0) {
          commitSegment(prevSegment);
        } else {
          setFoundUsers([]);
          setNotFound(false);
          setQuery("");
        }
        return;
      }
    },
    [query, selected, onSelect]
  );

  const toCurrentSegment = (val: string) => {
    if (val.includes("; ")) {
      const afterLast = val.split(";").pop() ?? "";
      return afterLast.replace(/^\s+/, "");
    }
    return val;
  };
  const format = (arr: SelectedMentor[]) =>
    arr.map((m) => `${m.isTag ? "@" : ""}${m.label.replace("@", "").replace(";", "")}`).join("; ");

  useEffect(() => {
    if (defaultValue === undefined || defaultValue === lastDefaultRef.current) return;
    const parsed = defaultValue
      .split(";")
      .map((s) => s.trim())
      .filter(Boolean)
      .map((label) => ({ label, isTag: label.startsWith("@") }));

    setSelected(parsed);
    onSelect(format(parsed));
    lastDefaultRef.current = defaultValue;
  }, [defaultValue, onSelect]);

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      if (actualQuery) {
        triggerSearch({ query: actualQuery, type: "users", limit: 10 })
          .unwrap()
          .then((res) => {
            const users = (res.results ?? [])
              .filter((r) => r.type === "user")
              .map((r) => r.data as UserResponse)
              .filter((u) => !selected.some((s) => s.label.replace(/^@/, "") === u.username));

            setFoundUsers(users);
            setNotFound(users.length === 0);
          })
          .catch(() => {
            setFoundUsers([]);
            setNotFound(true);
          });
      } else {
        setFoundUsers([]);
        setNotFound(false);
      }
    }, 300);

    return () => clearTimeout(delayDebounce);
  }, [actualQuery, triggerSearch]);

  const showResultBlock = actualQuery.length > 0 && (foundUsers.length > 0 || notFound);

  const selecthendler = (user: UserResponse) => {
    const newUsername = user.username;
    if (selected.find((s) => s.label.replace("@", "") === newUsername)) return;

    const valueToAdd = {
      label: isTagSearch ? `@${newUsername}` : `${newUsername}`,
      isTag: isTagSearch,
    };

    const updated = [...selected, valueToAdd];
    setSelected(updated);
    onSelect(format(updated));
    setQuery("");
    if (onInputChange) onInputChange("");
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if ((e.key === " " || e.key === ";" || e.key === "," || e.key === ".") && inputValue?.trim()) {
      e.preventDefault();

      commitSegment(inputValue);

      onInputChange?.("");
      return;
    }

    if (e.key === "Backspace" && query === "" && selected.length > 0) {
      setSelected((prev) => {
        const next = prev.slice(0, -1);
        onSelect(format(next));
        return next;
      });
    }
  };
  const showNotFoundMentionFallback = notFound && query.startsWith("@");
  const hasResults = foundUsers.length > 0;
  const shouldShowNotFound = showNotFoundMentionFallback;

  const shouldRenderResultList = showResultBlock && (hasResults || shouldShowNotFound);

  const normalizedQuery = actualQuery.startsWith("@") ? actualQuery.slice(1) : actualQuery;

  return (
    <Box sx={{ marginTop: "28px" }}>
      <Typography sx={styles.label}>Tranding Mentors</Typography>

      <Box sx={styles.inputMentorBox}>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: "4px", alignItems: "center", flex: 1 }}>
          {selected.map((item, idx) => (
            <Box key={idx} sx={{ display: "flex", alignItems: "center" }}>
              <Typography
                sx={{
                  fontFamily: "Inter",
                  fontWeight: 500,
                  fontSize: "14px",
                  lineHeight: "22px",
                  color: item.isTag ? theme.palette.custom.mainBlue : "#2A2D31",
                  textDecoration: item.isTag ? "underline" : "none",
                }}
              >
                {item.label}
              </Typography>
              <Typography
                sx={{
                  fontFamily: "Inter",
                  fontWeight: 500,
                  fontSize: "14px",
                  lineHeight: "22px",
                  color: "#2A2D31",
                }}
              >
                ;
              </Typography>
            </Box>
          ))}

          <InputBase
            placeholder={selected.length === 0 ? "Start writing a mentor's nickname via @." : ""}
            value={inputValue !== undefined ? inputValue : query}
            onChange={(e) => handleInputChange(e.target.value)}
            onKeyDown={handleKeyDown}
            sx={styles.inputMentor}
          />
        </Box>
      </Box>

      {shouldRenderResultList && (
        <Box sx={styles.resultList}>
          {hasResults ? (
            foundUsers.map((u) => (
              <MentorResultItem
                key={u.uid}
                avatarUrl={typeof u.image === "string" ? u.image : u.image?.url || ""}
                name={u.name}
                username={u.username}
                onClick={() => selecthendler(u)}
              />
            ))
          ) : showNotFoundMentionFallback ? (
            <Box>
              <Typography sx={styles.notFoundText}>
                Unfortunately, we didn’t find any users searching for @{normalizedQuery}
              </Typography>

              <Box
                sx={styles.TextBoxWrapper}
                onClick={() => {
                  if (isTagSearch) {
                    commitSegment(normalizedQuery);

                    setQuery("");
                    onInputChange?.("");
                  } else {
                    setQuery(normalizedQuery);
                    onInputChange?.(normalizedQuery);
                  }
                }}
              >
                <Typography sx={styles.fallbackText}>
                  Use this name {normalizedQuery} without a mention
                </Typography>
                <Box sx={styles.ArrowWrapper}>
                  <ArrowSvgRight />
                </Box>
              </Box>
            </Box>
          ) : null}
        </Box>
      )}
    </Box>
  );
};

export default TradingMentors;
